import React, { useState } from "react";
import {
  Box, Text, Title, Progress, Grid, Collapse, Group, Tooltip, ActionIcon
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import PacingVariance from "./pacingVariance";
import { useEngagementScore } from "../../../utils/calculateEngagement"; // ✅ keep your combined file
import actionTypes from "components/utils/actionTypes";

export default function EngagementDashboard({ actions, sessions, topics, sessionFrequency, sessionCountsByTopic }) {
  const compactView = useMediaQuery('(max-width: 1200px) and (min-width: 992px)');
  const engagement = useEngagementScore({
    actions,
    sessions,
    topics,
    sessionFrequency
  });

  const [openTopicIds, setOpenTopicIds] = useState([]);
  const [showTips, setShowTips] = useState(false);

  if (!engagement) return (
    <Box mt="xs">
      <Title color="gray" order={5} tt="uppercase">Engagement Level</Title>
      <Title color="gray" order={6} style={{ marginTop: 12 }}>No sessions available</Title>
    </Box>);

  const {
    level,
    description,
    score,
    explanations,
    tips,
    avgGap,
    expectedGap,
    topicStatuses
  } = engagement;

  const toggleTopic = (id) => {
    setOpenTopicIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const totalActions = actions.filter(a => a.status !== 'Cancelled').length;
  const statusCounts = Object.entries(actionTypes).reduce((acc, [status]) => {
    acc[status] = actions.filter((a) => a.status === status).length;
    return acc;
  }, {});
  const completedCount = statusCounts['Completed'] || 0;
  const completionRate = totalActions > 0 ? (completedCount / totalActions) * 100 : 0;

 return (
   <Grid gutter={compactView ? "md" : "xl"} wrap="wrap">
     {/* LEFT COLUMN */}
     <Grid.Col span={12} sm={12} md={12} lg={6} xl={6}>
       <Box mb="lg">
         <Title color="gray" order={5} tt="uppercase">Engagement Level</Title>
          <Title
                  order={4}
                  color="red"
                  //onClick={() => setShowTips(!showTips)}
                 style={{marginTop: 10 }}> {level}</Title>
         <Title color="gray" order={5} fw={400}>{description}</Title>
       </Box>

       <Box mb="lg">
          <Group spacing={6} align="center" mb="xs">
          <Title color="gray" order={6} tt="uppercase">
            Pacing Variance
          </Title>
        </Group>
         <PacingVariance
           sessionFrequency={sessionFrequency}
           actualGap={avgGap}
           expectedGap={expectedGap}
         />
       </Box>

       <Box mb="lg">
         <Group spacing={6} align="center" mb="xs" style={{ marginBottom: 20 }}>
          <Title color="gray" order={6} tt="uppercase">
            Action Completion Rate
          </Title>
        </Group>
         <Progress
           value={completionRate}
           size="xl"
           radius="xl"
           color="red"
           style={{ maxWidth: 290, marginTop: 4 }}
           label={`${Math.round(completionRate)}%`}
         />
       </Box>
     </Grid.Col>

     {/* RIGHT COLUMN */}
     <Grid.Col span={12} sm={12} md={12} lg={6} xl={6}>
     {/* Showing tips to improve engagement score
     {showTips && (
      <Box
        style={{
          marginTop: 10,
          marginBottom: 30
        }}
      >

        <Box mb="xs">
          <Title order={6} color="gray">Why your score is what it is:</Title>
          <ul style={{ paddingLeft: 18 }}>
            {explanations.map((explanation, i) => (
              <li key={`ex-${i}`}>
                <Title order={6} fw={500} color="gray.7">{explanation}</Title>
              </li>
            ))}
          </ul>
        </Box>

        <Box>
          <Title order={6} color="gray"> What you can do:</Title>
          <ul style={{ paddingLeft: 18 }}>
            {tips.map((tip, i) => (
              <li key={`tip-${i}`}>
                <Title order={6} fw={500} color="gray.7"> {tip}</Title>
              </li>
            ))}
          </ul>
        </Box>
      </Box>
    )}*/}
       <Box>
         <Group spacing={6} align="center" mb="xs">
          <Title color="gray" order={6} tt="uppercase">
            Topic Progression
          </Title>
        </Group>
         {topicStatuses.map((topic) => {
           const { topicId, name, status, actions: topicActions } = topic;
           const isOpen = openTopicIds.includes(topicId);
           const color =
             status === "Up to Date" ? "green" :
             status === "In Progress" ? "orange" :
             "red";

           const sessionCount = sessionCountsByTopic[topicId] || 0;
        
           return (
             <Box key={topicId} mb="sm">
               <Box
                 onClick={() => toggleTopic(topicId)}
                 style={{
                   display: "flex",
                   justifyContent: "space-between",
                   alignItems: "center",
                   padding: "10px 12px",
                   backgroundColor: "#f8f9fa",
                   borderTopLeftRadius: 8,
                   borderTopRightRadius: 8,
                   borderBottomLeftRadius: isOpen ? 0 : 8,
                   borderBottomRightRadius: isOpen ? 0 : 8,
                   border: "1px solid #e0e0e0",
                   borderBottom: isOpen ? "none" : "1px solid #e0e0e0",
                   cursor: "pointer",
                 }}
               >
                 <Title order={5} fw={500}>{name}</Title>
                 <Title order={5} color="gray.6">{isOpen ? "▴" : "▾"}</Title>
               </Box>

               <Collapse in={isOpen}>
                 <Box
                   bg="gray.0"
                   p="sm"
                   style={{
                     borderBottomLeftRadius: 8,
                     borderBottomRightRadius: 8,
                     border: "1px solid #e0e0e0",
                     borderTop: "none",
                   }}
                 >
                   <Group position="apart" spacing="md" mb="xs">
                     <Box style={{ display: 'flex', alignItems: 'center' }}>
                       <Title order={5} fw={600} style={{ marginRight: 8 }}>Status:</Title>
                       <Title order={5} fw={600} color={color}>{status}</Title>
                     </Box>
                     <Box style={{ display: 'flex', alignItems: 'center' }}>
                       <Title order={5} fw={600} style={{ marginRight: 8 }}>Sessions: </Title>
                       <Title order={5} fw={600}>{sessionCount}</Title>
                     </Box>
                   </Group>

                   <Title order={5} fw={600}>Actions:</Title>
                   <Progress
                     size="sm"
                     radius="xl"
                     sections={
                       topicActions.total > 0
                         ? [
                             { value: (topicActions.completed / topicActions.total) * 100, color: 'green' },
                             { value: (topicActions.started / topicActions.total) * 100, color: 'orange' },
                             { value: (topicActions.open / topicActions.total) * 100, color: 'gray' },
                           ]
                         : []
                     }
                     mb={4}
                   />
                   <Title order={6} color="gray.6" fw={500}>
                     {topicActions.completed} completed | {topicActions.started} started | {topicActions.open} open of {topicActions.total} total
                   </Title>
                 </Box>
               </Collapse>
             </Box>
           );
         })}
       </Box>
     </Grid.Col>
   </Grid>
 );
}
